import React, { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, ContactShadows } from '@react-three/drei';
import PanelBall from './PanelBall';
import { useStudio } from '@/lib/store';
import { Move, Maximize2, Minimize2, RotateCcw, RotateCw } from 'lucide-react';

function CanvasCapture({ onReady }) {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    if (!onReady) return;
    onReady(() => {
      gl.render(scene, camera);
      return gl.domElement.toDataURL('image/png');
    });
    // Effect intentionally runs once — gl/scene/camera are stable within a Canvas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function StudioLights() {
  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 8, 5]} intensity={1.15} castShadow shadow-mapSize={[2048, 2048]} />
      <directionalLight position={[-6, -3, -4]} intensity={0.35} color="#a5c8ff" />
      <pointLight position={[0, -6, 0]} intensity={0.25} />
    </>
  );
}

export default function Viewport3D({ onReady }) {
  const showHandles = useStudio((s) => s.showHandles);
  const isDraggingVertex = useStudio((s) => s.isDraggingVertex);
  const isDraggingPanel = useStudio((s) => s.isDraggingPanel);
  const panelDragMode = useStudio((s) => s.panelDragMode);
  const setPanelDragMode = useStudio((s) => s.setPanelDragMode);
  const panelManipMode = useStudio((s) => s.panelManipMode);
  const setPanelManipMode = useStudio((s) => s.setPanelManipMode);
  const scalePanel = useStudio((s) => s.scalePanel);
  const rotatePanel = useStudio((s) => s.rotatePanel);
  const pushHistory = useStudio((s) => s.pushHistory);
  const nudgeScale = (factor) => { pushHistory(); scalePanel(panelDragMode, factor); };
  const nudgeRotate = (rad) => { pushHistory(); rotatePanel(panelDragMode, rad); };

  return (
    <div className="absolute inset-0 r3f-canvas" data-testid="viewport-3d">
      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [3.2, 2.2, 3.4], fov: 40 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
      >
        <color attach="background" args={['#F1F3F5']} />
        <StudioLights />
        <PanelBall />
        <ContactShadows position={[0, -1.35, 0]} opacity={0.35} scale={8} blur={2.4} far={3} />
        <OrbitControls
          makeDefault
          enabled={!isDraggingVertex && panelDragMode == null}
          enablePan={false}
          minDistance={2.6}
          maxDistance={8}
          enableDamping
          dampingFactor={0.08}
        />
        <CanvasCapture onReady={onReady} />
      </Canvas>
      {panelDragMode != null && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 studio-glass px-3 py-2 rounded-full text-sm font-medium text-slate-800 shadow-lg"
          data-testid="panel-drag-banner"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          <span className="text-xs text-slate-600">Panel P{panelDragMode + 1}</span>

          <div className="flex items-center gap-0.5 bg-white/70 rounded-full p-0.5 ml-1" data-testid="panel-manip-mode">
            {[
              { id: 'move',   label: 'Move',   Icon: Move },
              { id: 'scale',  label: 'Scale',  Icon: Maximize2 },
              { id: 'rotate', label: 'Rotate', Icon: RotateCw },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setPanelManipMode(id)}
                data-testid={`btn-panel-manip-${id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
                  panelManipMode === id ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>

          {panelManipMode === 'scale' && (
            <div className="flex items-center gap-1 ml-1" data-testid="panel-scale-controls">
              <button
                onClick={() => nudgeScale(0.9)}
                data-testid="btn-panel-shrink"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                title="Shrink"
              >
                <Minimize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => nudgeScale(1.1)}
                data-testid="btn-panel-grow"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                title="Grow"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {panelManipMode === 'rotate' && (
            <div className="flex items-center gap-1 ml-1" data-testid="panel-rotate-controls">
              <button
                onClick={() => nudgeRotate(-Math.PI / 24)}
                data-testid="btn-panel-rotate-ccw"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                title="Rotate −7.5°"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => nudgeRotate(Math.PI / 24)}
                data-testid="btn-panel-rotate-cw"
                className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                title="Rotate +7.5°"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <button
            onClick={() => setPanelDragMode(null)}
            data-testid="btn-exit-panel-mode"
            className="ml-1 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs"
          >
            Done
          </button>
        </div>
      )}
      <div className="hidden sm:block absolute bottom-4 left-4 studio-glass px-3 py-2 rounded-xl text-xs text-slate-600 font-mono pointer-events-none" data-testid="viewport-hint">
        {panelDragMode != null
          ? panelManipMode === 'move'
            ? `sliding panel P${panelDragMode + 1} — drag to rotate on the sphere`
            : `${panelManipMode} panel P${panelDragMode + 1} — use +/− in the top pill`
          : isDraggingVertex
          ? 'editing vertex — rotation paused'
          : isDraggingPanel
          ? 'sliding panel…'
          : `drag = orbit · scroll = zoom${showHandles ? ' · grab handles to reshape · double-click a panel to slide it · double-click a vertex to round its corner' : ''}`}
      </div>
    </div>
  );
}
